import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Wand2, ImageIcon, RefreshCcw, Check, Send, Loader2, Share2, ExternalLink,
  Instagram, Facebook, Twitter, Linkedin, Youtube, Settings2, MousePointerClick, Video,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const PROVIDERS = [
  { k: "facebook",  l: "Facebook",  icon: Facebook,  color: "from-blue-600 to-blue-800" },
  { k: "instagram", l: "Instagram", icon: Instagram, color: "from-pink-500 to-fuchsia-700" },
  { k: "twitter",   l: "X",         icon: Twitter,   color: "from-slate-800 to-black" },
  { k: "linkedin",  l: "LinkedIn",  icon: Linkedin,  color: "from-sky-600 to-blue-800" },
  { k: "youtube",   l: "YouTube",   icon: Youtube,   color: "from-red-600 to-red-800" },
];

const CATS = ["restaurant", "product", "service", "property", "job", "matrimonial", "salon", "hospital", "school", "shopping", "event", "other"];
const TONES = ["friendly", "professional", "punchy", "luxury"];

export default function AutoListing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("brief");     // brief | preview | published
  const [brief, setBrief] = useState({
    category: "restaurant", audience: "local residents", tone: "friendly",
    key_points: "", city: "", want_image: true, listing_url: "",
  });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pubResult, setPubResult] = useState(null);
  const [selectedProviders, setSelectedProviders] = useState(["facebook", "twitter"]);

  const generate = async () => {
    if (!brief.key_points.trim()) { toast.error("Add a few key points"); return; }
    setBusy(true);
    try {
      const r = await api.post("/autolistings", {
        ...brief,
        key_points: brief.key_points.split("\n").map(s => s.trim()).filter(Boolean),
      });
      setDraft(r.data);
      setStep("preview");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setBusy(false); }
  };

  const regenerate = async (nudge) => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await api.post(`/autolistings/${draft.brief_id}/regenerate`, { nudge });
      setDraft({ ...draft, content: r.data.content, approved: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const remakeImage = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await api.post(`/autolistings/${draft.brief_id}/image`);
      setDraft({ ...draft, image_url: r.data.image_url });
    } catch (e) { toast.error(e?.response?.data?.detail || "Image failed"); }
    finally { setBusy(false); }
  };

  const generateVideo = async () => {
    if (!draft) return;
    if (!window.confirm("Generate a 5-second Sora 2 clip? This takes ~30–60s and uses AI credits.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/autolistings/${draft.brief_id}/video`);
      setDraft({ ...draft, video_url: r.data.video_url });
      toast.success("Video generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Video generation failed"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/autolistings/${draft.brief_id}/approve`);
      setDraft({ ...draft, approved: true, status: "approved" });
      toast.success("Approved — ready to publish");
    } catch (e) { toast.error("Approve failed"); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!draft.approved) { await approve(); }
    setBusy(true);
    try {
      const r = await api.post(`/autolistings/${draft.brief_id}/publish`, { providers: selectedProviders });
      setPubResult(r.data);
      setStep("published");
      toast.success("Published");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusy(false); }
  };

  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to use AI Auto-Listing.</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="autolisting-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2"><Sparkles className="text-fuchsia-500" size={22} />
            <h1 className="heading text-2xl font-black text-stone-900">AI Auto-Listing</h1>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">Create → AI writes copy + image → Approve → Publish everywhere.</p>
        </div>
        <Link to="/social-settings" className="text-xs font-bold text-stone-500 hover:text-stone-900 inline-flex items-center gap-1" data-testid="link-social-settings">
          <Settings2 size={12} /> Accounts
        </Link>
      </div>

      {/* Stepper */}
      <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
        {["brief", "preview", "published"].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`px-2 py-1 rounded-full ${step === s ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-500"}`}>{i + 1}. {s}</div>
            {i < 2 && <div className="flex-1 h-[1px] bg-stone-200" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Brief */}
      {step === "brief" && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 bg-white border border-stone-200 rounded-2xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <select value={brief.category} onChange={e => setBrief({ ...brief, category: e.target.value })} className="border border-stone-300 rounded-lg px-3 py-2 text-sm" data-testid="al-category">
              {CATS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={brief.tone} onChange={e => setBrief({ ...brief, tone: e.target.value })} className="border border-stone-300 rounded-lg px-3 py-2 text-sm" data-testid="al-tone">
              {TONES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <Input placeholder="Target audience (e.g. Working parents in Mumbai)" value={brief.audience} onChange={e => setBrief({ ...brief, audience: e.target.value })} data-testid="al-audience" />
          <Input placeholder="City (optional)" value={brief.city} onChange={e => setBrief({ ...brief, city: e.target.value })} />
          <Input placeholder="Existing listing URL (optional)" value={brief.listing_url} onChange={e => setBrief({ ...brief, listing_url: e.target.value })} />
          <Textarea rows={5} placeholder="Key points (one per line) — what should the AI emphasise?" value={brief.key_points} onChange={e => setBrief({ ...brief, key_points: e.target.value })} data-testid="al-keypoints" />
          <label className="flex items-center gap-2 text-xs text-stone-600">
            <input type="checkbox" checked={brief.want_image} onChange={e => setBrief({ ...brief, want_image: e.target.checked })} data-testid="al-want-image" />
            Also generate a marketing image (Nano Banana)
          </label>
          <Button onClick={generate} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="al-generate-btn">
            {busy ? <><Loader2 className="animate-spin mr-1.5" size={14} /> Generating…</> : <><Wand2 className="mr-1.5" size={14} /> Generate with AI</>}
          </Button>
        </motion.div>
      )}

      {/* Step 2 — Preview */}
      {step === "preview" && draft && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 bg-white border border-stone-200 rounded-2xl overflow-hidden">
          {draft.image_url && <img loading="lazy" src={draft.image_url} alt="" className="w-full max-h-72 object-cover" />}
          {draft.video_url && (
            <video src={draft.video_url} controls className="w-full max-h-72 bg-black" data-testid="al-video-player" />
          )}
          <div className="p-4 space-y-2">
            <div className="font-black text-lg text-stone-900">{draft.content?.title}</div>
            <div className="text-sm text-stone-700 whitespace-pre-wrap">{draft.content?.body}</div>
            <div className="flex flex-wrap gap-1">
              {draft.content?.hashtags?.map(h => <span key={h} className="text-[11px] bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded font-bold">#{h}</span>)}
            </div>
            <div className="text-xs text-emerald-700 font-bold">{draft.content?.cta}</div>
          </div>
          <div className="p-3 border-t border-stone-100 bg-stone-50 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => regenerate("make it 20% shorter and punchier")} disabled={busy} className="rounded-full text-xs" data-testid="al-regen-shorter">
              <RefreshCcw size={12} className="mr-1" /> Regenerate
            </Button>
            <Button variant="outline" onClick={remakeImage} disabled={busy} className="rounded-full text-xs" data-testid="al-regen-image">
              <ImageIcon size={12} className="mr-1" /> New image
            </Button>
            {!draft.video_url && (
              <Button variant="outline" onClick={generateVideo} disabled={busy} className="rounded-full text-xs" data-testid="al-gen-video">
                <Video size={12} className="mr-1" /> Add 5s video (Sora 2)
              </Button>
            )}
            <div className="flex-1" />
            {!draft.approved && (
              <Button onClick={approve} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs" data-testid="al-approve">
                <Check size={12} className="mr-1" /> Approve
              </Button>
            )}
          </div>

          {/* Publish targets */}
          <div className="p-4 border-t border-stone-100">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Publish to</div>
            <div className="flex gap-2 flex-wrap">
              {PROVIDERS.map(p => {
                const Icon = p.icon;
                const on = selectedProviders.includes(p.k);
                return (
                  <button key={p.k} onClick={() => setSelectedProviders(on ? selectedProviders.filter(x => x !== p.k) : [...selectedProviders, p.k])}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${on ? `text-white bg-gradient-to-r ${p.color}` : "bg-stone-100 text-stone-600"}`}
                    data-testid={`al-target-${p.k}`}>
                    <Icon size={12} /> {p.l}
                  </button>
                );
              })}
            </div>
            <Button onClick={publish} disabled={busy || selectedProviders.length === 0} className="mt-3 w-full rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="al-publish">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <><Send className="mr-1.5" size={14} /> Publish to {selectedProviders.length} network(s)</>}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Step 3 — Published */}
      {step === "published" && pubResult && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-3">
          <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white rounded-2xl p-5">
            <Sparkles size={20} className="mb-2" />
            <div className="font-black text-lg">Live on {pubResult.results.length} networks!</div>
            <div className="text-xs opacity-80 mt-1 break-all">Trackable link: {pubResult.share_url}</div>
            <div className="mt-3 text-[11px] inline-flex items-center gap-1 bg-white/10 px-2 py-1 rounded-full"><MousePointerClick size={11} /> Clicks come back to Find2Near for leads / chats / sales</div>
          </div>
          <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
            {pubResult.results.map((r, i) => (
              <div key={i} className="p-3 flex items-center gap-3" data-testid={`al-result-${r.provider}`}>
                {(() => {
                  const meta = PROVIDERS.find(p => p.k === r.provider) || {};
                  const Icon = meta.icon || Share2;
                  return <div className={`w-9 h-9 rounded-full grid place-items-center text-white bg-gradient-to-br ${meta.color || "from-stone-500 to-stone-800"}`}><Icon size={16} /></div>;
                })()}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold uppercase">{r.provider}</div>
                  <div className="text-[11px] text-stone-500">{r.status} · {r.message || "OK"}</div>
                </div>
                {r.post_url && (
                  <a href={r.post_url} target="_blank" rel="noreferrer" className="text-xs text-[#E05E36] font-bold inline-flex items-center gap-1">
                    View <ExternalLink size={11} />
                  </a>
                )}
              </div>
            ))}
          </div>
          <Button variant="outline" onClick={() => { setStep("brief"); setDraft(null); setPubResult(null); }} className="w-full rounded-full" data-testid="al-new-btn">
            Create another
          </Button>
        </motion.div>
      )}
    </div>
  );
}
